import { useQuery, keepPreviousData, UseQueryResult } from '@tanstack/react-query';
import { weatherService } from '@services/weather/weather';
import { WeatherDay } from '@models/weather.model';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';

export const WEATHER_QUERY_KEY = 'weather';

interface UseProjectWeatherArgs {
  projectId: string | undefined;
  from: string | null;
  to: string | null;
  /** Skip the query entirely for a project with no coordinates — never worth a round trip. */
  enabled?: boolean;
}

/**
 * Sprint 25, story-133. Keyed by organizationId, projectId and the visible range, per the repo's
 * org-scoped-hook convention. `refetchOnWindowFocus` realizes "come back and it's filled" (D3) —
 * a lazily-backfilled or newly-forecast row shows up without the user doing anything but
 * returning to the tab. `keepPreviousData` avoids a blank flash when the visible range only
 * shifts (e.g. paging one month forward). Never awaited by the caller before first paint — the
 * calendar always renders immediately; this hook's data fills in glyphs afterward.
 */
export const useProjectWeather = ({
  projectId,
  from,
  to,
  enabled = true,
}: UseProjectWeatherArgs): UseQueryResult<Map<string, WeatherDay>, Error> => {
  const { organizationId } = useAppSelector((state: RootState) => state.organization);

  return useQuery({
    queryKey: [WEATHER_QUERY_KEY, organizationId, projectId, from, to],
    queryFn: () => weatherService.getProjectWeather(projectId as string, from as string, to as string),
    enabled: enabled && !!organizationId && !!projectId && !!from && !!to,
    staleTime: 5 * 60 * 1000, // "a few minutes" — matches the backend's own 4-hour refresh cadence
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    select: (days) => new Map(days.map((day) => [day.date, day])),
  });
};

export default useProjectWeather;
